import { type FC, useEffect } from "react";
import { Link } from "react-router";
import { getActiveHolidayNotice, type HolidayNotice } from "../../../../constants/holidayNotice";
import { Container } from "../../components/Container";
import { formatDate, type OrderDetailDTO, summaryStatusLabel } from "../../components/format";
import { HolidayNoticeBanner } from "../../components/HolidayNoticeBanner";
import { useDecodedOrderCode } from "../../hooks/useDecodedOrderCode";
import { useOrder } from "../../hooks/useOrder";
import { ActionItemsSection } from "./ActionItemsSection";
import { ActionsSection } from "./ActionsSection";
import { DeliveryStatusSection } from "./DeliveryStatusSection";
import { IncludedServicesSection } from "./IncludedServicesSection";
import { ItemsSection } from "./ItemsSection";
import { OnHoldNotice } from "./OnHoldNotice";
import { PaymentSection } from "./PaymentSection";
import { RefundInfoSection } from "./RefundInfoSection";
import { ReturnRequestStatusCard } from "./ReturnRequestStatusCard";
import { ShippingSection } from "./ShippingSection";

type Props = {
  /**
   * 表示する長期休暇の告知。既定では現在日 (JST) から判定する。Storybook / テストで
   * 「告知あり / なし」を現在日に左右されず固定できるよう props で差し込めるようにしている。
   */
  holidayNotice?: HolidayNotice | null;
};

export const OrderDetailPage: FC<Props> = ({
  holidayNotice = getActiveHolidayNotice("orders"),
}) => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }

  if (result.status === "not-found") {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-sm">注文番号 {code}</p>
        <h1 className="mb-6 font-medium text-2xl text-neutral-900 tracking-tight">
          ご注文が見つかりませんでした
        </h1>
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  if (result.status === "error") {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-sm">注文番号 {code}</p>
        <h1 className="mb-6 font-medium text-2xl text-neutral-900 tracking-tight">
          注文情報を取得できませんでした
        </h1>
        <p className="mb-6 text-neutral-600 text-sm">{result.message}</p>
        <button
          type="button"
          onClick={() => {
            void result.refetch();
          }}
          className="text-sm underline underline-offset-4 hover:text-neutral-900"
        >
          再試行する
        </button>
      </Container>
    );
  }

  const order = result.order;

  return (
    <Container>
      <Link
        to="/dashboard"
        className="mb-6 inline-block text-neutral-500 text-xs hover:text-neutral-900"
      >
        ← 注文履歴
      </Link>

      <header className="mb-10 space-y-2">
        <p className="text-neutral-500 text-xs">注文番号 {order.code}</p>
        <h1 className="font-medium text-2xl text-neutral-900 tracking-tight">
          {summaryStatusLabel(order)}
        </h1>
        <p className="text-neutral-500 text-sm">{formatDate(order.createdAt)} に注文</p>
      </header>

      {/* 開いてすぐに何をすればよいかが分かるよう、お願いしていることを最上部にまとめる
          (支払い前の 7 日の期限もここで伝える) */}
      <ActionItemsSection order={order} />

      <DevAddressSourceLog order={order} />
      {/* 出荷が止まるだけでなく返送物の受領・検品も止まるため、配達済みの注文にも告知する */}
      <HolidayNoticeBanner notice={holidayNotice} />
      <OnHoldNotice order={order} />
      <RefundInfoSection order={order} />
      <ReturnRequestStatusCard order={order} />
      <ActionsSection order={order} />
      <ItemsSection order={order} />
      <IncludedServicesSection order={order} />
      <DeliveryStatusSection order={order} />
      <ShippingSection order={order} />
      <PaymentSection order={order} />
    </Container>
  );
};

/**
 * 開発時に「お届け先表示が Logiless / Shopify どちらの値か」を browser console に出すだけのコンポーネント。
 * `import.meta.env.DEV` (Vite) で本番ビルドでは何も出さない。
 */
const DevAddressSourceLog: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[mypage/order-detail] shippingAddress source:", order.shippingAddressSource, {
      address: order.shippingAddress,
      orderCode: order.code,
    });
  }, [order]);
  return null;
};
